/**
 * Doc System Commands — frontend handlers for document UI operations
 * broadcast from tool-runtime via broadcastCmd → pendingCmds → commandRegistryStore.
 *
 * Operations that genuinely require the browser:
 *  - doc_undo / doc_redo   — ProseMirror undo stack lives in the browser
 *  - doc_download          — browser File download API
 *  - doc_toggle_toc        — UI panel state
 *  - doc_open_comments     — UI panel state
 *  - help_chat_open/close  — Crisp support widget
 */

import { SelfContainedCommand, CommandExecutionContext } from '../../types/commands';
import { StandardToolResult } from '../../types/standardToolResults';
import useSpaceStore from '../../stores/spaceStore';
import { getDocsAppStore } from '../../apps/DocsApp/store/docsAppStore';
import { exportToWord, exportToPDF, exportToMarkdown, exportToPlainText } from '../../apps/DocsApp/utils/exportUtils';
import { supportChatService } from '../../services/chat/supportChatService';
import { createFileLogger } from '../../utils/logger';

const log = createFileLogger('DOC_SYSTEM_COMMANDS', { enabled: true });

// ─── helpers ──────────────────────────────────────────────────────────────────

/**
 * Find the open editor window for a given entity ID (accepts both _id and llmId).
 * Returns { windowId, spaceId } or null if no window is open for that entity.
 */
function findDocWindow(entityId: string): { windowId: string; spaceId: string } | null {
  const state = useSpaceStore.getState();
  const windows = state.windows;
  const spaceId = state.activeSpace?.id ?? '';

  for (const [windowId, winData] of Object.entries(windows)) {
    const entity = state.getWindowEntity(windowId);
    if (entity && (entity._id === entityId || entity.llmId === entityId || entity.id === entityId)) {
      return { windowId, spaceId };
    }

    // Also check entityId / entityLlmId stored directly on the window object
    const w = winData as any;
    if (w.entityId === entityId || w.entityLlmId === entityId) {
      return { windowId, spaceId };
    }
  }

  return null;
}

// ─── doc_undo ─────────────────────────────────────────────────────────────────

const doc_undo_cmd: SelfContainedCommand = {
  id: 'doc_undo',
  description: 'Undo the last change in an open document editor window.',
  schema: { parameters: [{ name: 'entityId', type: 'string', required: true, description: 'Document entity ID' }] },
  handler: async (params: any, _ctx: CommandExecutionContext): Promise<StandardToolResult> => {
    const { entityId } = params;
    const win = findDocWindow(entityId);
    if (!win) {
      return { status: 'error', message: `Document ${entityId} is not open in any window`, format: 'plaintext' };
    }
    log.info('[doc_undo] Dispatching undo for window:', win.windowId);
    window.dispatchEvent(new CustomEvent('docs:undo', { detail: { windowId: win.windowId, spaceId: win.spaceId } }));
    return { status: 'success', message: 'Undo dispatched', format: 'plaintext' };
  },
};

// ─── doc_redo ─────────────────────────────────────────────────────────────────

const doc_redo_cmd: SelfContainedCommand = {
  id: 'doc_redo',
  description: 'Redo the last undone change in an open document editor window.',
  schema: { parameters: [{ name: 'entityId', type: 'string', required: true, description: 'Document entity ID' }] },
  handler: async (params: any, _ctx: CommandExecutionContext): Promise<StandardToolResult> => {
    const { entityId } = params;
    const win = findDocWindow(entityId);
    if (!win) {
      return { status: 'error', message: `Document ${entityId} is not open in any window`, format: 'plaintext' };
    }
    log.info('[doc_redo] Dispatching redo for window:', win.windowId);
    window.dispatchEvent(new CustomEvent('docs:redo', { detail: { windowId: win.windowId, spaceId: win.spaceId } }));
    return { status: 'success', message: 'Redo dispatched', format: 'plaintext' };
  },
};

// ─── doc_download ─────────────────────────────────────────────────────────────

const doc_download_cmd: SelfContainedCommand = {
  id: 'doc_download',
  description: 'Download / export a document as docx, pdf, markdown, or plaintext.',
  schema: {
    parameters: [
      { name: 'entityId', type: 'string', required: true, description: 'Document entity ID' },
      { name: 'format', type: 'string', required: true, description: 'Export format: docx | pdf | markdown | plaintext' },
    ],
  },
  handler: async (params: any, _ctx: CommandExecutionContext): Promise<StandardToolResult> => {
    const { entityId, format } = params;

    const win = findDocWindow(entityId);
    if (!win) {
      return { status: 'error', message: `Document ${entityId} is not open in any window — open it first`, format: 'plaintext' };
    }

    const store = getDocsAppStore(win.windowId, win.spaceId);
    const { editorView } = store.getState();

    if (!editorView) {
      return { status: 'error', message: 'Editor view not ready — the document may still be loading', format: 'plaintext' };
    }

    const entity = useSpaceStore.getState().getWindowEntity(win.windowId);
    const documentTitle = entity?.name ?? 'Document';

    try {
      switch (format) {
        case 'docx':
          await exportToWord({ documentTitle, prosemirrorDoc: editorView.state.doc });
          break;
        case 'pdf':
          await exportToPDF({ documentTitle, prosemirrorDoc: editorView.state.doc });
          break;
        case 'markdown':
          await exportToMarkdown({ documentTitle, editorState: editorView.state });
          break;
        case 'plaintext':
          await exportToPlainText({ documentTitle, editorState: editorView.state });
          break;
        default:
          return { status: 'error', message: `Unknown format "${format}". Use: docx, pdf, markdown, plaintext`, format: 'plaintext' };
      }
      log.info(`[doc_download] "${documentTitle}" exported as ${format}`);
      return { status: 'success', message: `"${documentTitle}" exported as ${format}`, format: 'plaintext' };
    } catch (err: any) {
      log.error('[doc_download] Export failed:', err);
      return { status: 'error', message: `Export failed: ${err.message}`, format: 'plaintext' };
    }
  },
};

// ─── doc_toggle_toc ───────────────────────────────────────────────────────────

const doc_toggle_toc_cmd: SelfContainedCommand = {
  id: 'doc_toggle_toc',
  description: 'Toggle the Table of Contents panel in an open document editor.',
  schema: { parameters: [{ name: 'entityId', type: 'string', required: true, description: 'Document entity ID' }] },
  handler: async (params: any, _ctx: CommandExecutionContext): Promise<StandardToolResult> => {
    const { entityId } = params;
    const win = findDocWindow(entityId);
    if (!win) {
      return { status: 'error', message: `Document ${entityId} is not open in any window`, format: 'plaintext' };
    }
    log.info('[doc_toggle_toc] Toggling TOC for window:', win.windowId);
    window.dispatchEvent(new CustomEvent('docs:toggleTOC', { detail: { windowId: win.windowId, spaceId: win.spaceId } }));
    return { status: 'success', message: 'Table of Contents toggled', format: 'plaintext' };
  },
};

// ─── doc_open_comments ────────────────────────────────────────────────────────

const doc_open_comments_cmd: SelfContainedCommand = {
  id: 'doc_open_comments',
  description: 'Open the Comments panel in an open document editor.',
  schema: { parameters: [{ name: 'entityId', type: 'string', required: true, description: 'Document entity ID' }] },
  handler: async (params: any, _ctx: CommandExecutionContext): Promise<StandardToolResult> => {
    const { entityId } = params;
    const win = findDocWindow(entityId);
    if (!win) {
      return { status: 'error', message: `Document ${entityId} is not open in any window`, format: 'plaintext' };
    }
    log.info('[doc_open_comments] Opening comments for window:', win.windowId);
    window.dispatchEvent(new CustomEvent('open-comment-panel-for-new-comment', {
      detail: { windowId: win.windowId, spaceId: win.spaceId },
    }));
    return { status: 'success', message: 'Comments panel opened', format: 'plaintext' };
  },
};

// ─── help_chat_open ───────────────────────────────────────────────────────────

const help_chat_open_cmd: SelfContainedCommand = {
  id: 'help_chat_open',
  description: 'Open the support / help chat widget.',
  schema: { parameters: [] },
  handler: async (_params: any, _ctx: CommandExecutionContext): Promise<StandardToolResult> => {
    try {
      supportChatService.openChat();
      log.info('[help_chat_open] Support chat opened');
      return { status: 'success', message: 'Help chat opened', format: 'plaintext' };
    } catch (err: any) {
      return { status: 'error', message: `Failed to open help chat: ${err.message}`, format: 'plaintext' };
    }
  },
};

// ─── help_chat_close ──────────────────────────────────────────────────────────

const help_chat_close_cmd: SelfContainedCommand = {
  id: 'help_chat_close',
  description: 'Close the support / help chat widget.',
  schema: { parameters: [] },
  handler: async (_params: any, _ctx: CommandExecutionContext): Promise<StandardToolResult> => {
    try {
      supportChatService.closeChat();
      log.info('[help_chat_close] Support chat closed');
      return { status: 'success', message: 'Help chat closed', format: 'plaintext' };
    } catch (err: any) {
      return { status: 'error', message: `Failed to close help chat: ${err.message}`, format: 'plaintext' };
    }
  },
};

// ─── exports ──────────────────────────────────────────────────────────────────

export const DOC_SYSTEM_COMMANDS: SelfContainedCommand[] = [
  doc_undo_cmd,
  doc_redo_cmd,
  doc_download_cmd,
  doc_toggle_toc_cmd,
  doc_open_comments_cmd,
  help_chat_open_cmd,
  help_chat_close_cmd,
];
